import {
	QuadExpectedShareTargets,
	QuadMarginPremiumPointRanges,
	QuadMarginPremiumPointTargets,
} from '@marmon/shared/types/analysis';
import { QuadEightyTwentyRange } from '@marmon/shared/types/quad';

export const KristaProducts = [
	'driver_report',
	'qbr',
	'report',
	'summary',
] as const;

/**
 * Fallbacks when Remote Config is unavailable (local emulator).
 * Live values are `krista_prompt_version`, `krista_kb_version`, and
 * `krista_methodology_source`.
 */
export const DefaultKristaPromptVersion = '2026.08.1';
export const DefaultKristaKbVersion = '2026.08.1';
export const KristaMethodologySource = 'vizashift_methodology_thresholds_v1';

export const DriverReportProduct = 'driver_report' as const;
/** Default when a caller does not name a tab. The quad analysis tab. */
export const DriverReportTargetId = 'tab-quad-analysis' as const;
/**
 * One id per analysis tab. The cache key and the Krista payload both use it,
 * so each tab keeps its own driver report.
 */
export const DriverReportTargetIds = {
	quadQuartile: DriverReportTargetId,
	customerProduct: 'tab-customer-product',
	profit: 'tab-profit-analysis',
} as const;
export const DriverReportTargetType = 'quad_analysis_tab' as const;

export const ReportSlideProduct = 'report' as const;
export const ReportSlideTargetId = 'slide-quad-overview' as const;
export const ReportSlideTargetType = 'quad-overview' as const;

export const SummaryProduct = 'summary' as const;
/** One summary per calculation. The cache key still changes when the calc does. */
export const SummaryTargetId = 'analysis-summary' as const;
export const SummaryTargetType = 'analysis_summary' as const;

/**
 * Wider than a tab driver report. The summary is the whole analysis,
 * so more customers, products, and customer-product plots go with it.
 */
export const AnalysisSummaryEntityRowCaps = {
	sendAllMaxTotal: 500,
	maxPerEntityType: 200,
	topRevenueCount: 80,
	topZscoreCount: 40,
	bottomZscoreCount: 40,
	revenueShareAlwaysInclude: 0.005,
	negativeMarginRevenueShareFloor: 0.001,
} as const;

export const AnalysisSummaryCombinationRowCaps = {
	sendAllMaxTotal: 250,
	maxRows: 200,
	topRevenueCount: 80,
	topZscoreCount: 40,
	bottomZscoreCount: 40,
	revenueShareAlwaysInclude: 0.005,
	negativeMarginRevenueShareFloor: 0.001,
} as const;

/**
 * Krista returns HTTP 500 (`Start of joined stream header not found`) once the
 * JSON body reaches 101430 bytes. Stay under that cliff.
 */
export const KristaSubmitBodyByteLimit = 100_000;

export const QbrProduct = 'qbr' as const;
export const QbrTargetType = 'qbr_one_pager' as const;
export const QbrRequiredMonthCount = 12;

export const ReportSlideOutlierRowLimit = 8;

export const DefaultZscoreOutlierThreshold = 2;

export const DefaultDriverReportExpectedShares = {
	...QuadExpectedShareTargets,
} as const;

export const DefaultDriverReportMarginPremiumPts = {
	...QuadMarginPremiumPointTargets,
} as const;

export const DefaultDriverReportMarginPremiumRanges = {
	...QuadMarginPremiumPointRanges,
} as const;

export const DefaultEightyTwentyThreshold = QuadEightyTwentyRange.Eighty;

/**
 * Caps for entityRows. Z-scores are always computed on the full population first.
 */
export const DriverReportEntityRowCaps = {
	sendAllMaxTotal: 100,
	maxPerEntityType: 40,
	topRevenueCount: 15,
	topZscoreCount: 10,
	bottomZscoreCount: 10,
	revenueShareAlwaysInclude: 0.01,
	negativeMarginRevenueShareFloor: 0.0025,
} as const;

export const DriverReportCombinationRowCaps = {
	sendAllMaxTotal: 40,
	maxRows: 40,
	topRevenueCount: 15,
	topZscoreCount: 10,
	bottomZscoreCount: 10,
	revenueShareAlwaysInclude: 0.01,
	negativeMarginRevenueShareFloor: 0.0025,
} as const;

export const InsightsCollection = 'insights' as const;
export const InsightsApiPath = '/v1/insights' as const;

/** Krista routes every product through one events collection. */
export const KristaEventsPath = '/extension/api';

export const KristaQualityFlags = [
	'negative_contribution_margin',
	'material_cost_selected',
] as const;
